import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Trash2, Clock, IdCard } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { DAYS, coinToRp, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import RegionSelect, { addressFromKtp } from "@/components/RegionSelect";
import ImageUpload from "@/components/ImageUpload";
import { Empty, Img } from "@/components/common";
import { KycWizard } from "@/pages/KycCapture";

export function SubPage({ title, children, testid }) {
  const nav = useNavigate();
  return (
    <div data-testid={testid} className="fade-up">
      <div className="flex items-center gap-2 mb-5">
        <button data-testid="back-btn" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border grid place-items-center"><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="font-display text-xl font-extrabold">{title}</h1>
      </div>
      {children}
    </div>
  );
}

export function SaveBtn({ busy, children = "Simpan", testid = "save-btn", ...p }) {
  return (
    <button data-testid={testid} disabled={busy} {...p} className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}
    </button>
  );
}

function useSave(fn, msg, after) {
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      after && after();
    } catch (err) {
      toast.error(err.response ? errMsg(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  return [busy, save];
}

export function EditProfile() {
  const { user, refreshUser } = useAuth();
  const [f, setF] = useState({ name: user.name, whatsapp: user.whatsapp || "", address: user.address || {} });
  const [busy, save] = useSave(async () => { await api.put("/me", f); await refreshUser(); }, "Profil disimpan");
  const ktp = user.kyc?.details;
  const [ktpBusy, setKtpBusy] = useState(false);
  const fromKtp = async (silent) => {
    setKtpBusy(true);
    try {
      const address = await addressFromKtp(ktp);
      setF((x) => ({ ...x, address }));
      if (!silent) toast.success(address.village ? "Alamat diisi dari data KTP" : "Sebagian alamat diisi, lengkapi yang belum cocok");
    } catch (e) {
      if (!silent) toast.error("Gagal memuat data wilayah");
    } finally {
      setKtpBusy(false);
    }
  };
  useEffect(() => {
    if (ktp && !user.address?.province_id) fromKtp(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <SubPage title="Edit Profil" testid="edit-profile-page">
      <form onSubmit={save} className="space-y-4">
        <label className="block"><span className="text-xs font-semibold text-slate-600">Nama</span>
          <input data-testid="profile-name-input" className="field mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-600">Nomor WhatsApp</span>
          <input data-testid="profile-whatsapp-input" className="field mt-1" inputMode="tel" placeholder="08xxxxxxxxxx" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></label>
        <div>
          <div className="flex items-center justify-between mb-1"><p className="text-xs font-semibold text-slate-600">Alamat</p>
            {ktp && <button type="button" data-testid="address-from-ktp-btn" onClick={() => fromKtp(false)} disabled={ktpBusy} className="text-xs font-bold text-emerald-700 flex items-center gap-1">{ktpBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <IdCard className="w-3.5 h-3.5" />}Ambil dari data KTP</button>}</div>
          <RegionSelect value={f.address} onChange={(address) => setF({ ...f, address })} />
        </div>
        <SaveBtn busy={busy} testid="save-profile-btn" />
      </form>
    </SubPage>
  );
}

function meterColor(v) {
  if (v <= 100) return ["bg-red-500", "text-red-600"];
  if (v <= 200) return ["bg-lime-400", "text-lime-600"];
  if (v <= 300) return ["bg-emerald-500", "text-emerald-600"];
  return ["bg-emerald-800", "text-emerald-800"];
}

function AchievementMeter() {
  const { user, settings } = useAuth();
  const certs = Math.min(4, (user.worker?.certificates || []).length);
  const items = [["Foto profil", user.avatar ? 100 : 0, 100], ["Verifikasi identitas", user.kyc_status === "approved" ? 100 : 0, 100], [`Sertifikat (${certs}/4)`, certs * 50, 200]];
  const total = items.reduce((a, i) => a + i[1], 0);
  const [bar, txt] = meterColor(total);
  const fee = Math.max(0, (settings.fee_percent ?? 10) - (user.fee_discount || 0));
  return (
    <div data-testid="achievement-meter" className="bg-white rounded-2xl border p-4 mb-4">
      <div className="flex items-end justify-between mb-2"><p className="font-display font-bold">Meter Pencapaian</p><p className={`font-mono font-extrabold text-2xl ${txt}`} data-testid="achievement-value">{total}<span className="text-sm text-slate-400">/400</span></p></div>
      <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${bar} transition-[width] duration-700`} style={{ width: `${total / 4}%` }} /></div>
      <div className="mt-3 space-y-1">{items.map(([l, v, m]) => <p key={l} className="flex justify-between text-xs"><span className="text-slate-500">{l}</span><span className={`font-mono font-bold ${v ? "text-emerald-700" : "text-slate-400"}`}>{v}/{m}</span></p>)}</div>
      {user.is_worker && <p data-testid="worker-fee-percent" className="mt-3 text-xs bg-slate-50 rounded-lg px-3 py-2">Potongan aplikasi Anda: <b>{fee}%</b> per pekerjaan</p>}
    </div>
  );
}

export function KycPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const st = user.kyc_status;
  if (st === "pending" || st === "approved") {
    const k = user.kyc || {};
    return (
      <SubPage title="Verifikasi Identitas" testid="kyc-page">
        <AchievementMeter />
        <div data-testid="kyc-status" className={`rounded-2xl p-4 mb-4 ${st === "approved" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
          <p className="font-bold">{st === "approved" ? (k.auto_verified ? "Identitas terverifikasi otomatis" : "Identitas terverifikasi") : "Menunggu verifikasi admin"}</p>
          <p className="text-sm">{st === "approved" ? "Anda dapat mendaftar sebagai worker." : "Data KTP perlu dicek manual. Kami akan memberi tahu setelah diperiksa."}</p>
        </div>
        <div className="bg-white rounded-2xl border p-4 text-sm space-y-1">
          <p><span className="text-slate-500">NIK:</span> <span className="font-mono">{k.nik}</span></p>
          <p><span className="text-slate-500">Nama sesuai KTP:</span> {k.full_name}</p>
          <p><span className="text-slate-500">Tanggal lahir:</span> {k.birth_date}</p>
          <p><span className="text-slate-500">Diajukan:</span> {fmtDate(k.submitted_at)}</p>
          <div className="grid grid-cols-2 gap-2 pt-2">{k.ktp_photo && <Img path={k.ktp_photo} className="h-24 w-full rounded-xl" />}{k.selfie_photo && <Img path={k.selfie_photo} className="h-24 w-full rounded-xl" />}</div>
        </div>
        {st === "approved" && !user.is_worker && <div className="mt-4"><SaveBtn testid="go-worker-register-btn" onClick={() => nav("/profile/worker-register")}>Daftar sebagai Worker</SaveBtn></div>}
      </SubPage>
    );
  }
  return (
    <SubPage title="Verifikasi Identitas" testid="kyc-page">
      <AchievementMeter />
      {st === "rejected" && <p className="text-sm bg-red-50 text-red-700 rounded-xl px-3 py-2 mb-4">Pengajuan sebelumnya ditolak. Silakan ajukan ulang dengan data yang benar.</p>}
      <KycWizard />
    </SubPage>
  );
}

function SkillPicker({ value, onChange }) {
  const { settings } = useAuth();
  const toggle = (c) => onChange(value.includes(c) ? value.filter((x) => x !== c) : [...value, c]);
  return (
    <div className="flex flex-wrap gap-2" data-testid="skill-picker">
      {(settings.categories || []).map((c) => (
        <button type="button" key={c} data-testid={`skill-chip-${c}`} onClick={() => toggle(c)}
          className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${value.includes(c) ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-slate-200 text-slate-600"}`}>{c}</button>
      ))}
    </div>
  );
}

export function WorkerRegister() {
  const { refreshUser, setMode } = useAuth();
  const nav = useNavigate();
  const [skills, setSkills] = useState([]);
  const [agree, setAgree] = useState(false);
  const { settings } = useAuth();
  const [busy, save] = useSave(async () => {
    if (!skills.length) throw new Error("Pilih minimal 1 keahlian");
    if (!agree) throw new Error("Setujui syarat & ketentuan terlebih dahulu");
    await api.post("/worker/register", { skills, agree_terms: true });
    await refreshUser();
    setMode("worker");
  }, "Selamat! Anda terdaftar sebagai worker", () => nav("/"));
  return (
    <SubPage title="Daftar sebagai Worker" testid="worker-register-page">
      <p className="text-sm text-slate-600 mb-4">Data identitas diambil dari KYC Anda. Pilih keahlian yang ingin ditawarkan:</p>
      <SkillPicker value={skills} onChange={setSkills} />
      <h3 className="font-display font-bold mt-6 mb-2">Syarat & Ketentuan Worker</h3>
      <div data-testid="worker-terms-box" className="h-64 overflow-y-auto bg-white border rounded-2xl p-4">
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{settings.worker_terms}</p>
        <button type="button" data-testid="agree-terms-btn" onClick={() => setAgree(true)}
          className={`mt-5 w-full h-11 rounded-full font-bold text-sm transition-colors ${agree ? "bg-emerald-100 text-emerald-800" : "bg-amber-500 hover:bg-amber-600 text-white"}`}>{agree ? "✓ Anda telah setuju" : "Setuju"}</button>
      </div>
      <label className="flex items-start gap-2 mt-3 text-sm">
        <input data-testid="agree-terms-checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="w-4 h-4 mt-0.5 accent-emerald-600" />
        Saya setuju dengan persyaratan / Term and Service worker
      </label>
      <div className="mt-6"><SaveBtn busy={busy} disabled={busy || !agree || !skills.length} testid="worker-register-submit" onClick={save}>Daftar Worker</SaveBtn></div>
    </SubPage>
  );
}

export function SkillsPage() {
  const { user, refreshUser } = useAuth();
  const [skills, setSkills] = useState(user.worker?.skills || []);
  const [bio, setBio] = useState(user.worker?.bio || "");
  const [busy, save] = useSave(async () => { await api.put("/worker/profile", { skills, bio }); await refreshUser(); }, "Keahlian disimpan");
  return (
    <SubPage title="Keahlian & Kategori" testid="skills-page">
      <SkillPicker value={skills} onChange={setSkills} />
      <textarea data-testid="worker-bio-input" className="field h-24 py-2 mt-5" placeholder="Deskripsi singkat tentang Anda" value={bio} onChange={(e) => setBio(e.target.value)} />
      <div className="mt-5"><SaveBtn busy={busy} testid="save-skills-btn" onClick={save} /></div>
    </SubPage>
  );
}

export function CertificatesPage() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [path, setPath] = useState(null);
  const [busy, save] = useSave(async () => {
    if (!name || !path) throw new Error("Isi nama dan unggah sertifikat");
    await api.post("/worker/certificates", { name, path });
    setName(""); setPath(null);
    await refreshUser();
  }, "Sertifikat ditambahkan");
  const del = async (id) => { await api.delete(`/worker/certificates/${id}`); refreshUser(); };
  const certs = user.worker?.certificates || [];
  return (
    <SubPage title="Sertifikat Keahlian" testid="certificates-page">
      <div className="bg-white rounded-2xl border p-4 space-y-3 mb-5">
        <input data-testid="cert-name-input" className="field" placeholder="Nama sertifikat (mis. K3 Listrik)" value={name} onChange={(e) => setName(e.target.value)} />
        <ImageUpload testid="cert-upload" label="Unggah sertifikat" kind="certificate" value={path} onChange={setPath} />
        <SaveBtn busy={busy} testid="add-cert-btn" onClick={save}>Tambah Sertifikat</SaveBtn>
      </div>
      <div className="grid grid-cols-2 gap-3" data-testid="cert-list">
        {certs.length === 0 && <div className="col-span-2"><Empty title="Belum ada sertifikat" /></div>}
        {certs.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border overflow-hidden">
            <a href={fileUrl(c.path)} target="_blank" rel="noreferrer"><Img path={c.path} className="w-full h-24" /></a>
            <div className="p-2 flex items-center gap-1"><p className="text-xs font-bold flex-1 truncate">{c.name}</p>
              <button data-testid={`delete-cert-${c.id}`} onClick={() => del(c.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button></div>
          </div>
        ))}
      </div>
    </SubPage>
  );
}

export function PortfolioPage() {
  const { data } = useFetch("/worker/portfolio");
  return (
    <SubPage title="Portofolio" testid="portfolio-page">
      {!data ? <div className="h-24 bg-slate-200 rounded-2xl animate-pulse" /> : data.length === 0 ? <Empty title="Belum ada pekerjaan selesai" desc="Pekerjaan yang dikonfirmasi selesai otomatis masuk portofolio." /> : (
        <div className="space-y-3">{data.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border overflow-hidden">
            <div className="grid grid-cols-2"><Img path={p.before_photo} className="h-28 w-full" /><Img path={p.after_photo} className="h-28 w-full" /></div>
            <div className="p-3"><p className="font-bold">{p.title}</p><p className="text-xs text-slate-500">{p.category} · {fmtDate(p.completed_at)}</p></div>
          </div>))}</div>
      )}
    </SubPage>
  );
}

export function SchedulePage() {
  const { user, refreshUser } = useAuth();
  const [s, setS] = useState(user.worker?.schedule || {});
  const upd = (k, patch) => setS({ ...s, [k]: { ...(s[k] || { start: "08:00", end: "17:00" }), ...patch } });
  const [busy, save] = useSave(async () => { await api.put("/worker/profile", { schedule: s }); await refreshUser(); }, "Jadwal disimpan");
  return (
    <SubPage title="Jadwal Ketersediaan" testid="schedule-page">
      <div className="bg-white rounded-2xl border divide-y">
        {DAYS.map(([k, l]) => { const d = s[k] || {}; return (
          <div key={k} className="flex items-center gap-2 p-3">
            <label className="flex items-center gap-2 w-24 text-sm font-bold"><input data-testid={`schedule-${k}-toggle`} type="checkbox" checked={!!d.active} onChange={(e) => upd(k, { active: e.target.checked })} className="w-4 h-4 accent-amber-500" />{l}</label>
            {d.active ? (<><Clock className="w-4 h-4 text-slate-400" />
              <input data-testid={`schedule-${k}-start`} type="time" className="field h-9 w-24 px-2" value={d.start || "08:00"} onChange={(e) => upd(k, { start: e.target.value })} />
              <span className="text-slate-400">-</span>
              <input data-testid={`schedule-${k}-end`} type="time" className="field h-9 w-24 px-2" value={d.end || "17:00"} onChange={(e) => upd(k, { end: e.target.value })} /></>) : <span className="text-xs text-slate-400">Libur</span>}
          </div>); })}
      </div>
      <div className="mt-5"><SaveBtn busy={busy} testid="save-schedule-btn" onClick={save} /></div>
    </SubPage>
  );
}

const PAYOUT_STATUS = { pending: "Diproses", approved: "Berhasil", rejected: "Ditolak" };

export function PayoutPage() {
  const { user, refreshUser } = useAuth();
  const list = useFetch("/payouts/mine");
  const [f, setF] = useState({ coins: 10, method: "bank", provider: "BCA", account_name: user.name, account_number: "" });
  const providers = f.method === "bank" ? ["BCA", "BNI", "BRI", "Mandiri", "BSI"] : ["GoPay", "OVO", "DANA", "ShopeePay"];
  const [busy, save] = useSave(async () => { await api.post("/payouts", { ...f, coins: Number(f.coins) }); await refreshUser(); list.reload(); }, "Penarikan diajukan");
  return (
    <SubPage title="Penarikan Saldo" testid="payout-page">
      <p className="text-sm mb-4">Saldo tersedia: <b className="font-mono">{user.coins} koin</b> ({coinToRp(user.coins)})</p>
      <form onSubmit={save} className="bg-white rounded-2xl border p-4 space-y-3 mb-5">
        <input data-testid="payout-amount-input" type="number" min="10" className="field font-mono" value={f.coins} onChange={(e) => setF({ ...f, coins: e.target.value })} />
        <p className="text-xs text-slate-500 -mt-2">= {coinToRp(Number(f.coins) || 0)}</p>
        <div className="grid grid-cols-2 gap-2">
          {[["bank", "Rekening Bank"], ["ewallet", "E-Wallet"]].map(([k, l]) => (
            <button type="button" key={k} data-testid={`payout-method-${k}`} onClick={() => setF({ ...f, method: k, provider: k === "bank" ? "BCA" : "GoPay" })}
              className={`h-11 rounded-xl border-2 text-sm font-bold ${f.method === k ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>{l}</button>
          ))}
        </div>
        <select data-testid="payout-provider-select" className="field" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })}>{providers.map((p) => <option key={p}>{p}</option>)}</select>
        <input data-testid="payout-account-number" className="field font-mono" placeholder="Nomor rekening / HP" value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })} required />
        <input data-testid="payout-account-name" className="field" placeholder="Nama pemilik" value={f.account_name} onChange={(e) => setF({ ...f, account_name: e.target.value })} required />
        <SaveBtn busy={busy} testid="submit-payout-btn">Tarik Saldo</SaveBtn>
      </form>
      <div className="bg-white rounded-2xl border divide-y" data-testid="payout-history">
        {(list.data || []).map((p) => (
          <div key={p.id} className="p-3 flex items-center justify-between"><div><p className="text-sm font-bold">{p.coins} koin → {p.provider}</p><p className="text-[11px] text-slate-400">{p.account_number} · {fmtDate(p.created_at)}</p></div><span className="text-xs font-bold">{PAYOUT_STATUS[p.status]}</span></div>
        ))}
      </div>
    </SubPage>
  );
}

export function PasswordForm() {
  const [f, setF] = useState({ old_password: "", new_password: "" });
  const [busy, save] = useSave(async () => { await api.put("/auth/password", f); setF({ old_password: "", new_password: "" }); }, "Password berhasil diubah");
  return (
    <form onSubmit={save} className="space-y-3" data-testid="password-form">
      <input data-testid="old-password-input" type="password" className="field" placeholder="Password lama" value={f.old_password} onChange={(e) => setF({ ...f, old_password: e.target.value })} required />
      <input data-testid="new-password-input" type="password" className="field" placeholder="Password baru (min. 6)" minLength={6} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} required />
      <SaveBtn busy={busy} testid="save-password-btn">Ubah Password</SaveBtn>
    </form>
  );
}

function Chips({ options, value, onPick, testid }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map(([v, l]) => (
        <button type="button" key={v} data-testid={`${testid}-${v}`} onClick={() => onPick(v)}
          className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${value === v ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>{l}</button>
      ))}
    </div>
  );
}

function PinForm({ hasPin, onSaved }) {
  const [f, setF] = useState({ password: "", pin: "", confirm: "" });
  const [busy, save] = useSave(async () => {
    if (f.pin !== f.confirm) throw new Error("Konfirmasi PIN tidak sama");
    await api.post("/auth/pin", { password: f.password, pin: f.pin });
    setF({ password: "", pin: "", confirm: "" });
    onSaved();
  }, hasPin ? "PIN berhasil diubah" : "PIN dibuat, layar kunci aktif");
  const pinField = (k, ph) => <input data-testid={`pin-${k}-input`} type="password" inputMode="numeric" pattern="\d{4}" maxLength={4} required className="field font-mono tracking-[0.5em] placeholder:tracking-normal placeholder:font-sans" placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value.replace(/\D/g, "") })} />;
  return (
    <form onSubmit={save} className="space-y-3" data-testid="pin-form">
      <input data-testid="pin-password-input" type="password" required className="field" placeholder="Password akun" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">{pinField("pin", "PIN 4 angka")}{pinField("confirm", "Ulangi PIN")}</div>
      <SaveBtn busy={busy} testid="save-pin-btn">{hasPin ? "Ubah PIN" : "Buat PIN"}</SaveBtn>
    </form>
  );
}

function LockSettings() {
  const { user, refreshUser } = useAuth();
  const lock = user.lock || { enabled: false, timeout: 1, theme: "dark", has_pin: false };
  const update = async (patch) => {
    try {
      await api.put("/auth/lock", { enabled: lock.enabled, timeout: lock.timeout, theme: lock.theme, ...patch });
      await refreshUser();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="lock-settings" className="bg-white rounded-2xl border p-4 space-y-4 mt-6">
      <div className="flex items-center justify-between gap-3">
        <div><p className="font-display font-bold">PIN & Layar Kunci</p><p className="text-xs text-slate-500">Geser ke kanan lalu masukkan PIN untuk membuka aplikasi</p></div>
        {lock.has_pin && <button data-testid="lock-toggle" onClick={() => update({ enabled: !lock.enabled })}
          className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${lock.enabled ? "bg-emerald-600" : "bg-slate-300"}`}>
          <span className={`absolute left-0 top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${lock.enabled ? "translate-x-6" : "translate-x-1"}`} /></button>}
      </div>
      {lock.has_pin && lock.enabled && <>
        <div><p className="text-xs font-semibold text-slate-600 mb-2">Kunci setelah ditinggal</p>
          <Chips testid="lock-timeout" value={lock.timeout} onPick={(v) => update({ timeout: v })} options={[[1, "1 menit"], [5, "5 menit"], [10, "10 menit"], [20, "20 menit"]]} /></div>
        <div><p className="text-xs font-semibold text-slate-600 mb-2">Tampilan layar kunci</p>
          <Chips testid="lock-theme" value={lock.theme} onPick={(v) => update({ theme: v })} options={[["dark", "Gelap"], ["light", "Terang"]]} /></div>
      </>}
      <PinForm hasPin={lock.has_pin} onSaved={refreshUser} />
    </div>
  );
}

export function PasswordPage() {
  return <SubPage title="Ubah Password" testid="password-page"><PasswordForm /><LockSettings /></SubPage>;
}
